import { JsonLd } from 'react-schemaorg';
import { type Product as ProductSchema, type WithContext } from 'schema-dts';

import { type Reviews } from '../../../shared/reviews';
import { type Product } from '../../../shared/types';

type ProductMarkupProps = {
    readonly markupData: Product & Reviews;
};

const ProductMarkup = ({ markupData }: ProductMarkupProps): JSX.Element => {
    const { location } = window;
    const sevenDaysFromNow = new Date(Date.now() + 7 * 24 * 60 * 60 * 1_000).toString();
    const canAddRatingToMarkup = markupData.rating.value && markupData.rating.count;

    const markup: WithContext<ProductSchema> = {
        '@context': 'https://schema.org',
        '@type': 'Product',
        name: markupData.title,
        sku: markupData.manufacturerSku,
        mpn: markupData.manufacturerSku,
        brand: {
            '@type': 'Brand',
            name: markupData.brand,
        },
        image: markupData.images.map(image => image.absoluteUrl),
        description: markupData.productInformation.description.body,
        url: location.href,
        category: 'LeisureTimeActivity',
        ...(canAddRatingToMarkup && {
            aggregateRating: {
                '@type': 'AggregateRating',
                ratingValue: markupData.rating.value,
                reviewCount: markupData.rating.count,
                worstRating: 0,
                bestRating: 5,
            },
        }),
        offers: {
            '@type': 'Offer',
            priceCurrency: markupData.price.gross.currencyCode,
            price: markupData.price.gross.amount,
            priceValidUntil: sevenDaysFromNow,
            itemCondition: 'https://schema.org/NewCondition',
            availability: 'https://schema.org/InStock',
            seller: {
                '@type': 'Organization',
                name: 'Jochen-Schweizer GmbH',
            },
            url: location.href,
        },
        ...(markupData.reviews && {
            review: markupData.reviews.map(({ author, content, partner, rating, timestamps }) => ({
                '@type': 'UserReview',
                author: {
                    '@type': 'Person',
                    name: author.firstName,
                },
                datePublished: timestamps.created,
                reviewBody: content,
                ...((partner.venue || partner.zipCode) && {
                    contentLocation: {
                        '@type': 'Place',
                        address: {
                            '@type': 'PostalAddress',
                            addressLocality: partner.venue as string,
                            postalCode: partner.zipCode as string,
                        },
                    },
                }),
                ...(rating && {
                    reviewRating: {
                        '@type': 'Rating',
                        bestRating: 5,
                        ratingValue: rating,
                        worstRating: 0,
                    },
                }),
            })),
        }),
    };

    return <JsonLd<ProductSchema> item={markup} />;
};

export { ProductMarkup };
